import { Entity } from '../entity';
import { Address } from '../shared/address';
import { Location } from '../shared/location';
import { Site } from '../shared/site';
import { PhotoSettings } from '../photo.settings/photo.settings';
import { ContactType } from './contact.type';
import { EntitySubStatus } from '../entity.status/entity.status';

export enum CreditAccountType {
    Prepaid = 'Prepaid',
    Credit = 'Credit'
}

export enum CreditLimitOption {
    Warning = 'Warning',
    Block = 'Block'
}

export enum DueDateType {
    DueOnReceipt = 'Due on Receipt',
    XDayAfterTheBillInvoiceDate = 'X day(s) after the bill/invoice date',
    XDayAfterEndOfBillInvoiceMonth = 'X day(s) after end of bill/invoice month',
    XDayCurrentMonth = 'Xth day of the current month',
    XDayFollowingMonth = 'Xth day of the following month'
}

export enum SubContactType {
    Job = 'Job',
    Billing = 'Billing'
}

export enum InviteType {
    Invalid = 'Invalid',
    Valid = 'Valid',
    Connect = 'Connect',
    Enable = 'Enable',
    Invite = 'Invite',
    Resend = 'Resend',
    EnableLogin = 'Enable Login',
    ProvisionOwner = 'Provision Owner',
    ProvisionSCIM = 'Provision SCIM',
    None = 'None'
}

export enum AccessType {
    Employee = 'Employee',
    Subcontractor = 'Subcontractor',
    Client = 'Client'
}

export interface InvoiceOptions {
    enabled: boolean;
    email?: string;
    emailCC?: string[];
    address?: Address;
}

export interface SchedulingOptions {
    enabled: boolean;
    address?: Address;
    timezone?: string;
    tags?: Entity[];
}

export interface LoginOptions {
    invite: boolean;
    lastLogin?: Date;
    lastSeen?: Date;
    devices?: string[];
    system?: boolean;
    locked?: boolean;
    showWizard?: boolean;
}

export interface DispatchOptions {
    enabled?: boolean;
    email?: string;
    sendJsonFile?: boolean;
    sendPDFFile?: boolean;
    autoDispatch?: boolean;
}

export interface AccountingOptions {
    salesTaxRate?: Entity;
    purchaseTaxRate?: Entity;
    markupPercent?: number;
}

export interface ContactLocation {
    date: Date;
    location: Location;
}

export interface ParentContact extends Entity {
    type: ContactType;
    role?: Entity;
    login?: LoginOptions;
    primary?: boolean;
    companies?: Entity[];
}

export interface SocialNetwork {
    facebook?: string;
    linkedin?: string;
    instagram?: string;
    twitter?: string;
}

export interface DueDateOptions {
    value: number;
    type?: DueDateType;
    disabled?: boolean;
    min?: number;
    max?: number;
}

export interface CreditOptions {
    type: CreditAccountType;
    limitAmount?: number;
    limitBlock?: Entity;
    dueDate?: DueDateOptions;
    enforceOverdue?: boolean;
}

export interface BillToContact extends Entity {
    mobile?: string;
    email?: string;
}

export interface BillToAccount extends Entity {
    legalName?: string;
    phone?: string;
    email?: string;
    credit?: CreditOptions;
}

export interface BillToOptions {
    company?: BillToAccount | null;
    site?: Site | null;
    contact?: BillToContact | null;
}

export interface WorkBuddyConnectOptions {
    _id?: string;
    enabled: boolean;
    restrictJobTypes?: Entity[];
    link?: ContactConnectLink;
}

export type ConnectLinkStatus = 'pending' | 'connected' | 'disconnected';

export interface ContactConnectLink {
    status: ConnectLinkStatus;
    linkedTenant: string;
    linkedContactId: string;
    connectToken?: string;
    connectTokenHash?: string;
    inviteCode?: string;
    invitedAt?: Date;
    invitedBy?: Entity;
    recipientId?: string;
    connectedAt?: Date;
    disconnectedAt?: Date;
    disconnectedBy?: string;
    disconnectReason?: string;
}

export interface ContactEntity extends Entity {
    type: ContactType;
}

export interface ContactEntityWithStatus extends ContactEntity {
    status?: EntitySubStatus;
    parents?: ParentContact[];
}

export interface LoginContactEntity extends ContactEntity {
    dispatching?: DispatchOptions;
}

export interface SmsContactEntity extends ContactEntity {
    mobile?: string;
}

export interface EmailContactEntity extends ContactEntity {
    email?: string;
}

export interface NotifiableContactEntity extends SmsContactEntity, EmailContactEntity {
    group?: string;
}

export interface ContactPhotoSettings {
    overrideGlobalSettings: boolean;
    customPhotoSettings: PhotoSettings;
}

export interface TimesheetOptions {
    workRole?: Entity;
    hourlyCostRate?: number;
}

export interface CreditAmount {
    overdue: number;
    outstanding: number;
}
